import {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  CODE_LENGTH,
  EMPTY_CODE,
  INITIAL_CODE_LIFETIME,
} from '@/features/Profile/sections/ProfileSettings/components/EmailConfirmationModal/EmailConfirmationModal.constants';
import {
  EmailConfirmationResult,
  formatTime,
  getCodeCooldownDuration,
  getEmailConfirmationResultFromError,
  getFirstEmailConfirmationErrorMessage,
  loadCodeCooldownStarted,
  saveCodeCooldownStarted,
} from '@/features/Profile/sections/ProfileSettings/components/EmailConfirmationModal/EmailConfirmationModal.helpers';
import {
  CONFIRM_EMAIL_CONFIRMATION,
  ConfirmEmailConfirmationResponse,
  ConfirmEmailConfirmationVariables,
  CREATE_EMAIL_CONFIRMATION,
  CreateEmailConfirmationResponse,
  CreateEmailConfirmationVariables,
} from '@/features/Profile/sections/ProfileSettings/api';
import { GET_BASE_USER, GET_USER } from '@/api/queries/user.queries';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';

type TimerMode = 'lifetime' | 'cooldown';

interface UseEmailConfirmationModalParams {
  email: string;
  isOpen: boolean;
  onClose: () => void;
}

export function useEmailConfirmationModal({
  email,
  isOpen,
  onClose,
}: UseEmailConfirmationModalParams) {
  const { t } = useTranslation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const timerEndRef = useRef<number | null>(null);
  const requestedEmailRef = useRef<string | null>(null);
  const [code, setCode] = useState(EMPTY_CODE);
  const [errorMessage, setErrorMessage] = useState('');
  const [isCodeExpired, setIsCodeExpired] = useState(false);
  const [isCodeDisabled, setIsCodeDisabled] = useState(false);
  const [timerMode, setTimerMode] = useState<TimerMode | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSuccessOpen, toggleSuccess] = useToggleOpenState();
  const [codeCooldownStarted, setCodeCooldownStartedState] = useState(
    loadCodeCooldownStarted
  );
  const [createEmailConfirmation, { loading: isCreating }] = useMutation<
    CreateEmailConfirmationResponse,
    CreateEmailConfirmationVariables
  >(CREATE_EMAIL_CONFIRMATION, {
    errorPolicy: 'all',
  });
  const [confirmEmailConfirmation, { loading: isConfirming }] = useMutation<
    ConfirmEmailConfirmationResponse,
    ConfirmEmailConfirmationVariables
  >(CONFIRM_EMAIL_CONFIRMATION, {
    errorPolicy: 'all',
    refetchQueries: [GET_USER, GET_BASE_USER],
    awaitRefetchQueries: true,
  });

  function resetCode() {
    setCode(EMPTY_CODE);
    inputRefs.current[0]?.focus();
  }

  function setCodeInputRef(index: number, element: HTMLInputElement | null) {
    inputRefs.current[index] = element;
  }

  function clearMessages() {
    if (codeCooldownStarted) return;

    setErrorMessage('');
    setIsCodeExpired(false);
  }

  function setCodeCooldownStarted(value: boolean) {
    setCodeCooldownStartedState(value);
    saveCodeCooldownStarted(value);
  }

  function stopTimer() {
    timerEndRef.current = null;
    setTimerMode(null);
    setSecondsLeft(0);
  }

  function startTimer(mode: TimerMode, seconds: number) {
    stopTimer();

    if (mode === 'cooldown') {
      setCodeCooldownStarted(true);
    }

    timerEndRef.current = Date.now() + seconds * 1000;
    setTimerMode(mode);
    setSecondsLeft(seconds);
  }

  function resetModalState() {
    stopTimer();
    setCode(EMPTY_CODE);
    setIsCodeDisabled(false);
    clearMessages();
  }

  function getErrorMessage(errors?: EmailConfirmationResult['errors']) {
    return getFirstEmailConfirmationErrorMessage(
      errors,
      t('common.something_went_wrong')
    );
  }

  function handleCreateEmailConfirmationResult(
    result?: EmailConfirmationResult | null
  ) {
    const codeLifetime = result?.entity;

    if (typeof codeLifetime !== 'number') {
      setErrorMessage(getErrorMessage(result?.errors));
      return;
    }

    const codeCooldownDuration = getCodeCooldownDuration(codeLifetime);

    setIsCodeDisabled(false);
    resetCode();
    clearMessages();

    if (!codeCooldownStarted) {
      startTimer('lifetime', codeLifetime);
      return;
    }

    if (codeCooldownDuration > 0 && codeLifetime !== INITIAL_CODE_LIFETIME) {
      setIsCodeDisabled(true);
      setErrorMessage(
        t('pages.cabinet.email_confirmation.code_cooldown_message')
      );
      startTimer('cooldown', codeCooldownDuration);
      return;
    }

    setErrorMessage('');
    setCodeCooldownStarted(false);
    startTimer('lifetime', codeLifetime);
  }

  async function requestCode() {
    try {
      const response = await createEmailConfirmation({
        variables: { email },
      });
      handleCreateEmailConfirmationResult(
        response.data?.createEmailConfirmation
      );
    } catch (error) {
      const result = getEmailConfirmationResultFromError(
        error,
        'createEmailConfirmation'
      );
      if (result) {
        handleCreateEmailConfirmationResult(result);
        return;
      }

      setErrorMessage(t('common.something_went_wrong'));
    }
  }

  function handleConfirmEmailConfirmationResult(
    result?: EmailConfirmationResult | null
  ) {
    if (typeof result?.entity === 'number' && !result.errors.length) {
      onClose();
      resetModalState();
      toggleSuccess(true);
      return;
    }

    resetCode();
    setIsCodeExpired(false);
    setErrorMessage(getErrorMessage(result?.errors));

    if (typeof result?.entity !== 'number') return;

    const codeCooldownDuration = getCodeCooldownDuration(result.entity);
    if (codeCooldownDuration > 0) {
      setIsCodeDisabled(true);
      startTimer('cooldown', codeCooldownDuration);
      return;
    }

    setIsCodeDisabled(false);
  }

  async function handleConfirmCode(fullCode: string) {
    if (isConfirming) return;

    try {
      const response = await confirmEmailConfirmation({
        variables: { code: fullCode },
      });
      handleConfirmEmailConfirmationResult(
        response.data?.confirmEmailConfirmation
      );
    } catch (error) {
      const result = getEmailConfirmationResultFromError(
        error,
        'confirmEmailConfirmation'
      );
      if (result) {
        resetCode();
        setIsCodeExpired(false);
        setErrorMessage(getErrorMessage(result.errors));
        setIsCodeDisabled(true);

        if (timerEndRef.current) {
          const remainingLifetime = Math.max(
            0,
            Math.ceil((timerEndRef.current - Date.now()) / 1000)
          );
          const codeCooldownDuration =
            getCodeCooldownDuration(remainingLifetime);

          if (codeCooldownDuration > 0) {
            startTimer('cooldown', codeCooldownDuration);
            return;
          }
        }

        stopTimer();
        setIsCodeDisabled(false);
        return;
      }

      resetCode();
      setErrorMessage(t('common.something_went_wrong'));
    }
  }

  function applyCodeValue(index: number, value: string) {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH - index);
    const nextCode = [...code];

    if (!digits) {
      nextCode[index] = '';
    }

    digits.split('').forEach((digit, digitIndex) => {
      nextCode[index + digitIndex] = digit;
    });

    setCode(nextCode);
    clearMessages();

    if (!digits) return;

    const fullCode = nextCode.join('');
    if (fullCode.length === CODE_LENGTH) {
      handleConfirmCode(fullCode);
      return;
    }

    inputRefs.current[
      Math.min(index + digits.length, CODE_LENGTH - 1)
    ]?.focus();
  }

  function handleCodeChange(index: number, value: string) {
    if (isCodeDisabled || isCreating || isConfirming) return;

    applyCodeValue(index, value);
  }

  function handleCodeKeyDown(
    index: number,
    event: KeyboardEvent<HTMLInputElement>
  ) {
    if (event.key === 'Backspace' && !code[index]) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
      handleCodeChange(index - 1, '');
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
      inputRefs.current[index - 1]?.select();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
      inputRefs.current[index + 1]?.select();
    }
  }

  function handleCodePaste(
    index: number,
    event: ClipboardEvent<HTMLInputElement>
  ) {
    event.preventDefault();
    if (isCodeDisabled || isCreating || isConfirming) return;

    const pastedCode = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedCode) return;

    applyCodeValue(index, pastedCode);
  }

  function handleClose() {
    onClose();
    resetModalState();
    requestedEmailRef.current = null;
  }

  function handleSuccessClose() {
    toggleSuccess(false);
  }

  function handleTimerEnd(mode: TimerMode) {
    stopTimer();

    if (mode === 'lifetime') {
      setErrorMessage('');
      setIsCodeExpired(true);
      setIsCodeDisabled(true);
      resetCode();
      return;
    }

    setCodeCooldownStarted(false);
    setIsCodeDisabled(false);
  }

  useEffect(() => {
    if (!isOpen) return;
    if (requestedEmailRef.current === email) return;

    requestedEmailRef.current = email;
    requestCode();
  }, [email, isOpen]);

  useEffect(() => {
    if (!timerMode || !timerEndRef.current) return;

    const intervalId = setInterval(() => {
      if (!timerEndRef.current) return;

      const nextSecondsLeft = Math.max(
        0,
        Math.ceil((timerEndRef.current - Date.now()) / 1000)
      );

      setSecondsLeft(nextSecondsLeft);

      if (nextSecondsLeft === 0) {
        handleTimerEnd(timerMode);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [timerMode]);

  const timerText =
    timerMode === 'lifetime'
      ? t('pages.cabinet.email_confirmation.code_lifetime_timer', {
          time: formatTime(secondsLeft),
        })
      : t('pages.cabinet.email_confirmation.code_cooldown_timer', {
          time: formatTime(secondsLeft),
        });

  return {
    code,
    errorMessage,
    isCodeExpired,
    isCodeInputDisabled: isCodeDisabled,
    isCodeInputReadOnly: isCreating || isConfirming,
    isResendVisible: isOpen && !timerMode && !isCreating,
    isSuccessOpen,
    timerText,
    isTimerVisible: !!timerMode,
    handleClose,
    handleSuccessClose,
    setCodeInputRef,
    handleCodeChange,
    handleCodeKeyDown,
    handleCodePaste,
    requestCode,
  };
}
